import { useState } from 'react';
import { useConfiguration } from '../../../../../../store/configurationStore';
import { Label } from '../../../types';
import { CurrencySectionProps } from './types';
import { Currency } from '../../../../../../types/generatedTypes';
import { CURRENCIES, CurrencyData } from './currencyData';
import { useSectionData } from '../../../hooks/useSectionData';

export const useCurrencySection = (props: CurrencySectionProps) => {
  const { templateId, instrumentType, sectionId } = props;
  const {
    updateCurrencyMappings,
    addCurrencyVariation,
    removeCurrencyVariation,
  } = useConfiguration();

  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyData | null>(
    null
  );

  // Get section data using shared hook
  const { section, selectedColumn, validationResult } = useSectionData({
    templateId,
    instrumentType,
    sectionId,
  });

  // Extract currency mappings from section
  const currencyMappings = section?.config.currencyMappings || [];

  // Get available currencies (filtered to exclude already-added ones)
  const getAvailableCurrencies = (): CurrencyData[] => {
    const addedCurrencies = currencyMappings.map(m => m.currency);
    return CURRENCIES.filter(c => !addedCurrencies.includes(c.code));
  };

  // Add a new currency
  const addCurrency = (currency: Currency): void => {
    const existingMapping = currencyMappings.find(m => m.currency === currency);
    if (existingMapping) return; // Already exists

    const updatedMappings = [...currencyMappings, { currency, variations: [] }];
    updateCurrencyMappings(
      templateId,
      instrumentType,
      sectionId,
      updatedMappings
    );
  };

  // Remove a currency
  const removeCurrency = (currency: Currency): void => {
    const updatedMappings = currencyMappings.filter(
      m => m.currency !== currency
    );
    updateCurrencyMappings(
      templateId,
      instrumentType,
      sectionId,
      updatedMappings
    );
  };

  // Add variation to a currency
  const addVariation = (currency: Currency, variation: string): void => {
    const trimmedValue = variation.trim();
    if (!trimmedValue) {
      throw new Error('⚠️ Variation value cannot be empty');
    }

    const existingMapping = currencyMappings.find(m => m.currency === currency);
    if (!existingMapping) {
      throw new Error('⚠️ Currency mapping not found');
    }

    // Check if variation already exists (case-insensitive)
    const isDuplicate = existingMapping.variations.some(
      v => v.toLowerCase() === trimmedValue.toLowerCase()
    );

    if (isDuplicate) {
      throw new Error(`⚠️ Variation "${trimmedValue}" already exists for this currency`);
    }

    // Call store action with single variation - store reads current state
    addCurrencyVariation(
      templateId,
      instrumentType,
      sectionId,
      currency,
      trimmedValue
    );
  };

  // Remove variation from a currency
  const removeVariation = (currency: Currency, variationId: string): void => {
    const labels = formatVariationsAsLabels(currency);
    const labelToRemove = labels.find(l => l.id === variationId);
    if (!labelToRemove) return;

    // Call store action with variation to remove - store reads current state
    removeCurrencyVariation(
      templateId,
      instrumentType,
      sectionId,
      currency,
      labelToRemove.value
    );
  };

  // Format variations as labels for LabelChips
  const formatVariationsAsLabels = (currency: Currency): Label[] => {
    const mapping = currencyMappings.find(m => m.currency === currency);
    return (mapping?.variations || []).map((variation, index) => ({
      id: `${currency}-${index}`,
      value: variation,
    }));
  };

  // Get currency suggestions for autocomplete
  const getCurrencySuggestions = (): string[] => {
    return CURRENCIES.map(c => c.code);
  };

  return {
    currencyMappings,
    selectedCurrency,
    setSelectedCurrency,
    availableCurrencies: getAvailableCurrencies(),
    addCurrency,
    removeCurrency,
    formatVariationsAsLabels,
    removeVariation,
    addVariation,
    selectedColumn,
    currencySuggestions: getCurrencySuggestions(),
    validationResult,
  };
};
